import {Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ReplaySubject} from 'rxjs';
import {DeregistrationPack} from '../../interface/deregistration-pack';
import {Registration} from '../../interface/registration';
import {ListResponse} from '../../interface/response/list-response';
import {debounceTime, distinctUntilChanged, startWith, takeUntil} from 'rxjs/operators';
import {MatSelectChange} from '@angular/material/select';
import {DeregistrationPackService} from '../../services/deregistration-pack.service';
import {DEREGISTRATION_PACK_STATUS_NEW} from '../../services/dict.service';

@Component({
  selector: 'app-dict-deregistration-packs',
  templateUrl: './dict-deregistration-packs.component.html',
  styleUrls: ['./dict-deregistration-packs.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictDeregistrationPacksComponent)}
  ]
})
export class DictDeregistrationPacksComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() label: string;
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() filterByRegistration?: Registration;
  @Output() selectedDeregistrationPack: EventEmitter<DeregistrationPack>
    = new EventEmitter<DeregistrationPack>();

  externalControl = true;
  disabled = false;
  items: DeregistrationPack[] = [];
  value?: string = null;
  deregistrationPackFilterCtrl: FormControl;
  itemsSubject: ReplaySubject<ListResponse<DeregistrationPack>>
    = new ReplaySubject<ListResponse<DeregistrationPack>>(1);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private deregistrationPackService: DeregistrationPackService,
    private fb: FormBuilder,
  ) {
    this.deregistrationPackFilterCtrl = fb.control('');
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }

    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        takeUntil(this.destroyed$)
      )
      .subscribe((val: string) => {
        if (val) {
          let exists = false;
          this.items.forEach((deregistrationPack: DeregistrationPack) => {
            if (deregistrationPack.uuid === val) {
              exists = true;
            }
          });

          if (!exists) {
            this.deregistrationPackService.loadNotCountedList(0, 3, {
              uuid: val
            }, this.itemsSubject);
          }
        }
      });

    this.deregistrationPackFilterCtrl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((val) => {
        if (val) {
          if (this.filterByRegistration) {
            this.deregistrationPackService.loadNotCountedList(0, 10, {
              context: val,
              company: this.filterByRegistration.contract.company.id,
              insuranceCompany: this.filterByRegistration.insuranceCompany.id,
              statusKeys: [DEREGISTRATION_PACK_STATUS_NEW],
            }, this.itemsSubject);
          } else {
            this.deregistrationPackService.loadNotCountedList(0, 10, {
              context: val,
            }, this.itemsSubject);
          }
        } else if (this.control.value) {
          this.deregistrationPackService.loadNotCountedList(0, 10, {
            uuid: this.control.value
          }, this.itemsSubject);
        }
      });

    this.itemsSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: ListResponse<DeregistrationPack>) => {
        this.items = result.items;
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.items.forEach((deregistrationPack: DeregistrationPack) => {
      if (deregistrationPack.uuid === e.value) {
        this.selectedDeregistrationPack.emit(deregistrationPack);
      }
    });

    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
  }

}
